import React from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useField } from 'react-final-form';
import { makeStyles } from 'styles/util';
import strings from 'localization/strings';
import storageSiteBookingUpdateModes from 'enums/storageSiteBookingUpdateModes';
import { getStorageTitle } from 'helpers/StorageSiteHelper';

import MarginBox from 'common/MarginBox';
import Typography from '@material-ui/core/Typography';
import ShortDate from 'common/ShortDate';
import Amount from 'common/Amount';
import Button from '@material-ui/core/Button';
import { Radios, Checkboxes, TextField } from 'mui-rff';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

const useStyles = makeStyles(() => ({
    notLastLineOfBooking: {
        '&>td': {
            borderBottom: 'none',
        }
    }
}));

const PriceAffectedBookingsDialog = ({ priceAffectedBookings, open, onSave, onCancel }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const updateMode = useField('priceAffectedBookingsDialog.updateMode').input.value;
    const showPriceChangeEmailCustomMessageTextbox = useField('priceAffectedBookingsDialog.sendPriceChangeEmails').input.value;
    const showSendPriceChangeEmails = updateMode !== storageSiteBookingUpdateModes.noBookings.key;
    const showBookingCheckboxes = updateMode === storageSiteBookingUpdateModes.someActiveSubscriptionBookings.key;

    const updateModeOptions = Object.values(storageSiteBookingUpdateModes)
        .map(o => ({
            value: o.key,
            label: o.title
        }));

    const getLineInfo = priceAffectedBooking => {
        return {
            bookingId: priceAffectedBooking.existingBooking.id,
            tenantName: priceAffectedBooking.existingBooking.tenantActor.name,
            storageTitle: priceAffectedBooking.existingBooking.storage
                ? getStorageTitle(priceAffectedBooking.existingBooking.storage, priceAffectedBooking.existingBooking.storageGroup, appContext)
                : '',
            startDate: priceAffectedBooking.existingBooking.startDate,
            priceChangeInstants: priceAffectedBooking.priceChangeInstants,
            newAmountDiffersFromOldAmount: priceAffectedBooking.newAmountDiffersFromOldAmount,
            currency: priceAffectedBooking.existingBooking.currency
        };
    };

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={onCancel} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title" disableTypography>
                <Typography variant="h5">
                    {strings.priceAffectedBookingsDialogTitle}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <MarginBox bottom={2}>
                    <Typography variant="body1" gutterBottom>
                        {strings.storageSiteBookingUpdateModeDescription}
                    </Typography>

                    <Radios
                        name="priceAffectedBookingsDialog.updateMode"
                        data={updateModeOptions}
                    />
                </MarginBox>

                {
                    showSendPriceChangeEmails &&
                    (
                        <>
                            <MarginBox bottom={2}>
                                <Checkboxes
                                    name="priceAffectedBookingsDialog.sendPriceChangeEmails"
                                    data={{
                                        label: strings.sendPriceChangeEmails,
                                        value: true
                                    }}
                                />
                            </MarginBox>
                            {
                                showPriceChangeEmailCustomMessageTextbox &&
                                (
                                    <MarginBox bottom={2}>
                                        <TextField
                                            name="priceAffectedBookingsDialog.priceChangeEmailCustomMessage"
                                            label={strings.messageBody}
                                            type="text"
                                            variant="outlined"
                                            multiline
                                            rows={4}
                                            helperText={strings.priceChangeEmailCustomMessageHelperText}
                                        />
                                    </MarginBox>
                                )
                            }
                        </>
                    )
                }

                <Table>
                    <TableHead>
                        <TableRow>
                            {
                                showBookingCheckboxes && <TableCell/>
                            }
                            <TableCell>
                                {strings.bookingNumber}
                            </TableCell>
                            <TableCell>
                                {strings.tenant}
                            </TableCell>
                            <TableCell>
                                {strings.bookedStorageTitle}
                            </TableCell>
                            <TableCell>
                                {strings.startDate}
                            </TableCell>
                            <TableCell align="right">
                                {strings.currentPricePerMonth}
                            </TableCell>
                            <TableCell>
                                {strings.changeDate}
                            </TableCell>
                            <TableCell align="right">
                                {strings.newPricePerMonth}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            priceAffectedBookings?.map((priceAffectedBooking, bookingIndex) => {
                                const lineInfo = getLineInfo(priceAffectedBooking);
                                const priceChangeInstantsToInclude = lineInfo.priceChangeInstants
                                    .filter((priceChangeInstant, priceChangeInstantIndex) => priceChangeInstantIndex > 0 || priceChangeInstant.newAmount !== priceChangeInstant.previousAmount);
                                return (
                                    <React.Fragment key={lineInfo.bookingId}>
                                        {
                                            priceChangeInstantsToInclude
                                                .map((priceChangeInstant, priceChangeInstantIndex) => (
                                                    <TableRow key={priceChangeInstant.date} className={priceChangeInstantIndex < priceChangeInstantsToInclude.length - 1 ? classes.notLastLineOfBooking : undefined}>
                                                        {
                                                            showBookingCheckboxes &&
                                                            (
                                                                <TableCell>
                                                                    {
                                                                        priceChangeInstantIndex === 0 &&
                                                                        (
                                                                            <Checkboxes
                                                                                name={`priceAffectedBookingsDialog.bookings[${bookingIndex}].selected`}
                                                                                data={{
                                                                                    value: true
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                </TableCell>
                                                            )
                                                        }
                                                        <TableCell>
                                                            {
                                                                priceChangeInstantIndex === 0 && lineInfo.bookingId
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                priceChangeInstantIndex === 0 && lineInfo.tenantName
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                priceChangeInstantIndex === 0 && lineInfo.storageTitle
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                priceChangeInstantIndex === 0 && <ShortDate value={lineInfo.startDate}/>
                                                            }
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {
                                                                priceChangeInstantIndex === 0 && <Amount value={lineInfo.priceChangeInstants[0].previousAmount} currency={lineInfo.currency}/>
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                lineInfo.newAmountDiffersFromOldAmount && <ShortDate value={priceChangeInstant.date}/>
                                                            }
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {
                                                                lineInfo.newAmountDiffersFromOldAmount && <Amount value={priceChangeInstant.newAmount} currency={lineInfo.currency}/>
                                                            }
                                                            {
                                                                !lineInfo.newAmountDiffersFromOldAmount && <>{strings.unchanged}</>
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                        }
                                    </React.Fragment>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                >
                    {strings.save}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onCancel}
                >
                    {strings.cancel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

PriceAffectedBookingsDialog.propTypes = {
    priceAffectedBookings: PropTypes.array,
    open: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default PriceAffectedBookingsDialog;
